import React, {Component} from "react";
import {complex, add, multiply, sin, sqrt, pi, equal, sort, format, subtract, arg} from 'mathjs';
import dustinWilliamsonAvatar from "../../../assets/img/demo/dustin-williamson-avatar.png";
import Tools from '../../../Tools';

class CanvasBase extends Component {
    /**
     * Renders the canvas component.
     * @returns {React.ReactNode}
     */
    render = () => {
        const {resolution} = this.canvasConfig();
        const {width, height} = resolution;

        return (
            <div className="canvas-container">
                <canvas ref="canvas" width={width} height={height} style={this.canvasStyle()}/>
            </div>
        );
    };

    /**
     * Handler fire after load component.
     */
    componentDidMount() {
        this.initBackground();
    }

    /**
     * Initialize background of canvas.
     */
    initBackground() {
        const {resolution} = this.canvasConfig();
        const imageObject = new Image();
        const {width, height} = resolution;
        const background = this.canvasBackground();
        const {handleCanvasBackgroundLoaded} = this;

        imageObject.src = background;
        imageObject.onload = () => {
            try {

                this.drawImageTopLeft(complex(0, 0), background, width, height, () => {
                    if (typeof handleCanvasBackgroundLoaded === 'function') {
                        handleCanvasBackgroundLoaded();
                    }
                });

            } catch (e) {
                console.log(e
                )
            }
        };
    }

    /**
     * Returns the image source of canvas background.
     * @returns {React.ReactNode}
     */
    canvasBackground() {
        return undefined;
    }

    /**
     * Handler fire after load canvas background.
     * @returns {void}
     */
    handleCanvasBackgroundLoaded() {
    }

    /**
     * Draws line form point a to point b.
     * @param a {complex} The start point of the line.
     * @param b {complex} The end point of the line.
     * @param config {object} The override of default config.
     */

    drawLine(a, b, config) {
        const context = this.getContext();
        const {line} = this.canvasConfig(config);
        const {width} = line;
        context.lineWidth = width;

        context.beginPath();
        context.moveTo(this.offsetX(a.re), this.offsetY(a.im));
        context.lineTo(this.offsetX(b.re), this.offsetY(b.im));
        context.closePath();
        context.stroke();
    }

    /**
     * Draws dashed line form point a to point b.
     * @param a {complex} The start point of the line.
     * @param b {complex} The end point of the line.
     * @param config {object} The override of default config.
     */

    drawDashedLine(a, b, config) {
        const context = this.getContext();
        const {line} = this.canvasConfig(config);
        const {dash, space} = line;
        this.applyConfig(config);

        context.setLineDash([dash, space]);
        this.drawLine(a, b, config);
        context.setLineDash([]);
    }

    /**
     * Draws arrow form point a to point b.
     * @param a {complex} The point of the arrow tail.
     * @param b {complex} The point of the arrow vertex.
     * @param config {object} The override of default config.
     */
    drawDashedArrow(a, b, config) {
        this.drawDashedLine(a, b, config);
        this.drawFilledArrowHead(a, b, config);
    }

    /**
     * Draws filled arrow head on point b.
     * @param a {complex} The point of the arrow tail.
     * @param b {complex} The point of the arrow vertex.
     * @param config {object} The override of default config.
     */
    drawFilledArrowHead(a, b, config) {
        const {line} = this.canvasConfig(config);
        const {arrowHead} = line;
        const {length, filled, closed} = arrowHead;
        const theta = arg(subtract(b, a));

        const p1 = add(b, complex({abs: length, arg: theta + 0}));
        const p2 = add(b, complex({abs: length, arg: theta + pi / 2}));
        const p3 = add(b, complex({abs: length, arg: theta - pi / 2}));

        if (closed)
            this.drawPolygon([p1, p2, p3], config, filled);
        else
            this.drawOpenArrowHead(a, b, config);
    }

    /**
     * Draws open arrow head on point b.
     * @param a {complex} The point of the arrow tail.
     * @param b {complex} The point of the arrow vertex.
     * @param config {object} The override of default config.
     */
    drawOpenArrowHead(a, b, config) {
        const {line} = this.canvasConfig(config);
        const {arrowHead} = line;
        const {length} = arrowHead;
        const theta = arg(subtract(b, a));

        const p2 = add(b, complex({abs: length, arg: theta + pi + pi / 4}));
        const p3 = add(b, complex({abs: length, arg: theta + pi - pi / 4}));

        this.drawLine(b, p2, config);
        this.drawLine(b, p3, config);
    }

    /**
     * Draws curved arrow from point a point b passed on point q.
     * @param a {complex} The point of the arrow tail.
     * @param q {complex} The quadratic point on curved line.
     * @param b {complex} The point of the arrow vertex.
     * @param config {object} The override of default config.
     */
    drawCurvedArrow(a, q, b, config) {
        const context = this.getContext();
        this.applyConfig(config);

        context.beginPath();
        context.moveTo(this.offsetX(a.re), this.offsetY(a.im));
        context.quadraticCurveTo(this.offsetX(q.re), this.offsetY(q.im), this.offsetX(b.re), this.offsetY(b.im));
        context.stroke();

        this.drawFilledArrowHead(q, b, config);
    };

    /**
     * Draws polygon according to points.
     * @param points {array} The vertices of the polygon.
     * @param config {object} The override of default config.
     * @param filled {boolean} Determine if polygon filled.
     */
    drawPolygon(points, config, filled) {
        const context = this.getContext();

        context.moveTo(points[0].re, points[0].im);
        context.beginPath();
        for (let i = 0; i < points.length; i++) {
            const a = points[i];
            const b = points[(i + 1) % points.length];
            context.lineTo(this.offsetX(a.re), this.offsetY(a.im), this.offsetX(b.re), this.offsetY(b.im));
        }

        if (filled) context.fill();
        context.closePath();
        context.stroke();
    };


    /**
     * Draws filled polygon according to points.
     * @param points {array} The vertices of the polygon.
     * @param config {object} The override of default config.
     */
    drawFilledPolygon(points, config) {
        this.drawPolygon(points, config, true);
    };

    /**
     * Draws image according to image center.
     * @param point {complex} The coordinate of image center.
     * @param image {string} The image source.
     * @param width {integer} The image width on canvas.
     * @param height {integer} The image height on canvas.
     * @param callback {function} The callback call after complete drawing.
     */
    drawImage(point, image, width, height, callback) {
        const context = this.getContext();

        const imageObject = new Image();
        imageObject.src = image;
        imageObject.onload = () => {
            const ox = this.offsetX(point.re - width / 2);
            const oy = this.offsetY(point.im + height / 2);

            context.drawImage(imageObject, ox, oy, width, height);
            if (typeof callback === 'function')
                callback();
        };
    };


    /**
     * Draws image according to top-left image corner.
     * @param point {complex} The coordinate of image center.
     * @param image {string} The image source.
     * @param width {integer} The image width on canvas.
     * @param height {integer} The image height on canvas.
     * @param callback {function} The callback call after complete drawing.
     */
    drawImageTopLeft(point, image, width, height, callback) {
        const context = this.getContext();

        const imageObject = new Image();
        imageObject.src = image;
        imageObject.onload = () => {
            context.drawImage(imageObject, point.re, point.im, width, height);
            if (typeof callback === 'function')
                callback();
        }
    };


    /**
     * Draws the filled circle on point o with radius of r.
     * @param o {complex} The center of the circle.
     * @param r {number} The radius of the circle.
     * @param config {object} The override of default config.
     */
    drawFilledCircle(o, r, config) {
        const context = this.getContext();
        this.applyConfig(config);

        context.beginPath();
        context.arc(this.offsetX(o.re), this.offsetY(o.im), r, 0, 2 * pi, false);
        context.fill();
        context.closePath();
        context.stroke();
    }


    /**
     * Draws text on specific point in canvas.
     * @param point {complex} The position of the text.
     * @param text {string} The text witch appears on canvas.
     * @param config {object} The override of default config.
     */
    drawText(point, text, config) {
        const context = this.getContext();
        this.applyConfig(config);

        context.fillText(text, this.offsetX(point.re), this.offsetY(point.im));
    }

    // TODO: Write Doc.
    drawCircle(o, r, color, width, filled) {
        const context = this.getContext();
        context.strokeStyle = color;
        context.fillStyle = color;
        context.lineWidth = width;

        context.beginPath();
        context.arc(this.offsetX(o.re), this.offsetY(o.im), r, 0, 2 * pi, false);
        if (filled) context.fill();
        context.closePath();

        context.stroke();
    };

    /**
     * Allies config on canvas.
     * @param config {object} The override of default config.
     */
    applyConfig(config) {
        const context = this.getContext();
        const {line, strokeStyle, fillStyle, font, textAlign, textBaseline, className} = this.canvasConfig(config);

        const {width} = line;
        context.lineWidth = width;
        context.strokeStyle = strokeStyle;
        context.fillStyle = fillStyle;

        if (font) context.font = font;
        if (textAlign) context.textAlign = textAlign;
        if (textBaseline) context.textBaseline = textBaseline;
        if (className) context.className = className;
    }

    clear() {
        const {resolution} = this.canvasConfig();
        const {width, height} = resolution;
        const context = this.getContext();
        context.clearRect(0, 0, width, height);
    }

    getContext = () => this.getRef().getContext('2d');
    getRef = () => this.refs.canvas;

    offsetX = (x) => x;
    offsetY = (y) => y;

    baseConfig = () => ({
            width: 200,
            resolution: {width: 1000, height: 1335},
            line: {
                width: 14,
                dash: 8,
                space: 16,
                arrowHead: {
                    length: 6,
                    filled: true,
                    closed: true,
                },
            },
            strokeStyle: `${this.strokeStyle()}`,
            fillStyle: `${this.fillStyle()}`,
        }
    );
    canvasConfig = (config) => {
        let destination = this.baseConfig();
        Tools.deepExtend(destination, config ?? {});
        return destination;
    };

    strokeStyle = () => '#FFFFFF';
    fillStyle = () => '#FFFFFF';

    canvasStyle = () => {
        const {width: canvasWidth} = this.canvasConfig();
        return {
            width: canvasWidth,
        };
    };
}

export default CanvasBase;
